<template>
  <div class="add-payment" redesign>
    <!-- Header -->
    <div class="local-hero local-hero-dark no-padding">
      <div class="local-hero-graphic">
        <div class="local-hero-header-wrapper">
          <h1 class="local-hero-header" v-translate="{ orgName: organizationDetails.name }">
            Give to the %{orgName} {{organizationDetails.programName}}
          </h1>
        </div>
        <div class="local-hero-graphic-wrapper hidden-mobile">
          <div class="local-hero-graphic-holder tree-planting">
            <TreePlantingSvg :color="organizationDetails.darkBrandColor" />
          </div>
        </div>
      </div>
    </div>

    <div class="form-container contribution-form">
      <!-- Contribution Info Section -->
      <div v-if="userIsLoggedIn" class="form-header">
        <span v-if="organizationDetails.contributionCopy" v-html="contributionCopy" />
        <span
          v-else
          v-translate="{
            orgName: organizationDetails.name,
            orgFundBalance: utils.formatNumberAsUSD(organizationDetails.startingFundBalance),
          }"
        >
          The {{organizationDetails.programName}} is a community resource designed to help %{orgName} employees through
          financial hardships. The fund was kicked off with a gift of %{orgFundBalance}, but now we
          need everyone to help keep the circle going. Caring community members like you have set up
          recurring gifts via credit or debit card and payroll deductions ranging from $2-100 per
          month. Others have made a one-time gift ranging from $25-$2,500.
        </span>
      </div>
      <div v-else class="form-header">
        <span v-if="organizationDetails.contributionCopy" v-html="contributionCopy" />
      </div>
      <div class="form-field faq-cta">
        <span v-translate>
          For information on the tax status of donations,
        </span>
        <span>&nbsp;</span>
        <a :href="taxLink" target="_blank" v-translate>see the FAQ</a>.
      </div>
      <div class="form-field see-below">
        <span v-translate
          >Select how you would like to give and provide the information requested.</span
        >
      </div>
      <!-- End Contribution Info Section -->

      <!-- Display both payroll and credit card forms if org allows payroll. -->
      <TabContainer
        v-if="organizationDetails.allowsPayrollRequests"
        class="card-shape"
        @changed-tab="onChangedTab"
      >
      <!-- :selectedTabName="userIsLoggedIn ? tabTitles.payroll : tabTitles.creditCard" -->
        <Tab
          v-if="userIsLoggedIn"
          :name="tabTitles.payroll"
          :selected="selectedTabName == tabTitles.payroll"
        >
          <PayrollRequestForm />
        </Tab>
        <Tab :name="tabTitles.creditCard" :selected="selectedTabName == tabTitles.creditCard">
          <DonationForm v-if="userIsLoggedIn" />
          <AnonymousDonationForm v-else />
        </Tab>
      </TabContainer>

      <!-- Otherwise, only display credit card forms. -->
      <div v-if="!organizationDetails.allowsPayrollRequests" class="credit-card-form">
        <DonationForm v-if="userIsLoggedIn" />
        <AnonymousDonationForm v-else />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Tab from '@/components/Tab'
import TabContainer from '@/components/TabContainer'
import DonationForm from './_DonationForm'
import AnonymousDonationForm from './_AnonymousDonationForm'
import PayrollRequestForm from '@/views/donations/PayrollRequestForm'
import { analyticsMixin, pagePathWithAuthTracking } from '@/services/googleAnalytics'
import { getVariableReplacement } from '@/services/organizationCustomVariables'
import FAQ from '@/services/faqs'
import TreePlantingSvg from '@/views/svg/TreePlantingSvg'
import utils from '@/services/utils'

export default {
  name: 'AddPayment',
  mixins: [analyticsMixin],
  components: {
    Tab,
    TabContainer,
    DonationForm,
    AnonymousDonationForm,
    PayrollRequestForm,
    TreePlantingSvg,
  },
  data() {
    return {
      utils,
      selectedTabName: 'Credit/Debit Card',
      taxLink: null,
    }
  },
  computed: {
    ...mapState(['organizationDetails']),
    ...mapGetters(['userIsLoggedIn']),
    contributionCopy() {
      const varMap = { org: this.organizationDetails }
      return utils.formatValue(this.organizationDetails.contributionCopy, varMap)
    },
    tabTitles() {
      return {
        payroll: 'Payroll Deduction',
        creditCard: 'Credit/Debit Card',
      }
    },
  },
  created() {
    // Customize GA page title based on whether user first sees payroll.
    let title, path
    if (this.organizationDetails.allowsPayrollRequests) {
      title = 'Payroll'
      path = '/payroll'
    } else {
      title = 'AddPayment'
      path = '/add-payment'
    }
    this.trackCustomPageview(title, pagePathWithAuthTracking(path, this.userIsLoggedIn))

    if (this.userIsLoggedIn) this.selectedTabName = 'Payroll Deduction'

    if (this?.$route?.params?.donationType && this?.$route?.params?.donationType === 'donation') {
      if (this.$route.params.paymentType == 'Credit Card') {
        this.selectedTabName = 'Credit/Debit Card'
      } else {
        this.selectedTabName = 'Payroll Deduction'
      }
    }

    FAQ.api.getTcOrTaxFaq(this.organizationDetails.name, 'tax_q').then(faq => {
      this.taxLink = `/faq#${faq.faqOrder}`
    })
  },
  methods: {
    /* Emit GA pageviews every time user swaps tabs. */
    onChangedTab(newTab) {
      this.selectedTabName = newTab

      if (newTab === 'Payroll Deduction') {
        this.trackCustomPageview(
          'Payroll',
          pagePathWithAuthTracking('/payroll', this.userIsLoggedIn),
        )
      } else if (newTab === 'Credit/Debit Card') {
        this.trackCustomPageview(
          'AddPayment',
          pagePathWithAuthTracking('/add-payment', this.userIsLoggedIn),
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.tree-planting {
  align-items: end !important;
}

.add-payment {
  display: flex;
  flex-direction: column;
}

.contribution-form {
  @extend .constrain-tablet-and-up;

  .form-header {
    margin-bottom: 1rem;

    ::v-deep p {
      font-size: 1.25rem;
      line-height: 1.75rem;
    }
  }

  .faq-cta {
    font-size: 1rem;
    line-height: 1.5rem;
  }

  .see-below {
    font-size: 1.5rem;
    font-weight: bold;
    line-height: 2rem;
    margin-bottom: 2.5rem;
    padding: 0 1rem;
    text-align: center;
  }
}

.tab-container {
  width: 100%;
}
.credit-card-form {
  width: 100%;
}
.info-text {
  margin: 1rem 0;
}
.small-text {
  margin: 1rem 0;
  font-size: 0.7rem;
  color: $night-rider;
  margin-bottom: 1rem;
}

.contribution-info-header {
  width: 100%;
}
</style>
